import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Import all locale files
import enTranslations from '../locales/en.json';
import jaTranslations from '../locales/ja.json';
import cnTranslations from '../locales/cn.json';
import tcTranslations from '../locales/tc.json';

export type Locale = 'en' | 'ja' | 'cn' | 'tc';

interface LocalizationContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, any>) => string;
  getCurrentLanguageCode: () => string;
}

const translations: Record<Locale, any> = {
  en: enTranslations,
  ja: jaTranslations,
  cn: cnTranslations,
  tc: tcTranslations,
};

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

interface LocalizationProviderProps {
  children: ReactNode;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>('en');

  // Load saved locale from localStorage on mount
  useEffect(() => {
    const savedLocale = localStorage.getItem('locale') as Locale;
    if (savedLocale && ['en', 'ja', 'cn', 'tc'].includes(savedLocale)) {
      setLocaleState(savedLocale);
    }
  }, []);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
  };

  const t = (key: string, params?: Record<string, any>): string => {
    const keys = key.split('.');
    let value: any = translations[locale];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = keys.reduce((obj, k) => obj?.[k], translations.en) || key;
        break;
      }
    }

    let result = typeof value === 'string' ? value : key;

    // Replace parameters in the string
    if (params) {
      Object.keys(params).forEach(paramKey => {
        const regex = new RegExp(`\\{${paramKey}\\}`, 'g');
        result = result.replace(regex, String(params[paramKey]));
      });
    }

    return result;
  };

  const getCurrentLanguageCode = () => {
    switch (locale) {
      case 'en':
        return 'EN';
      case 'ja':
        return 'JA';
      case 'cn':
        return 'CN';
      case 'tc':
        return 'TC';
      default:
        return 'EN';
    }
  };

  return (
    <LocalizationContext.Provider value={{ locale, setLocale, t, getCurrentLanguageCode }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
}; 